import { Locked } from '@carbon/icons-react';
import { FloatingFocusManager, FloatingPortal, useFloating } from '@floating-ui/react';
import { ApiError } from '@dodoc/api-client';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountdown } from '@/shared/lib/hooks';
import { Avatar, Banner, Button, CodeInput, FormField, Input, RadioGroup, RecordingIndicator } from '@/ui';
import { describeError } from '../api/errors';
import type { ActiveRecording } from './ActiveRecordingContext';
import s from './SessionLock.module.css';
import type { OtpChallenge, OtpChannel } from './types';

export interface SessionLockProps {
  /** Имя пользователя; null — сессия заблокирована сервером до загрузки профиля. */
  userName: string | null;
  /** Минут бездействия до блокировки (политика сервера). */
  idleMinutes: number;
  /** На устройстве задан PIN — предлагаем его первым; иначе — код из SMS/WhatsApp. */
  pinEnabled: boolean;
  onUnlockWithPin: (pin: string) => Promise<void>;
  onRequestCode: (channel: OtpChannel) => Promise<OtpChallenge>;
  onUnlockWithCode: (challengeId: string, code: string) => Promise<void>;
  onSignOut: () => void;
  /** Идущая запись приёма: статус и «Стоп» видны поверх блокировки (ПОРТ-22). */
  recording?: ActiveRecording | null;
}

const PIN_MIN = 4;
const PIN_MAX = 6;
/** После 5 ошибок сервер сбрасывает PIN — дальше только код. */
const PIN_DEAD_CODES = new Set(['IDENTITY_PIN_LOCKED', 'IDENTITY_PIN_NOT_SET']);

/**
 * Экран блокировки после бездействия (ПОРТ-22). Закрывает данные полностью, остальной интерфейс
 * недоступен (inert); идущая запись приёма не прерывается и управляется поверх блокировки.
 */
export function SessionLock({ userName, idleMinutes, pinEnabled, onUnlockWithPin, onRequestCode, onUnlockWithCode, onSignOut, recording }: SessionLockProps) {
  const { t } = useTranslation('shell');
  const { t: tErrors, i18n } = useTranslation('errors');
  const titleId = useId();
  const textId = useId();
  const [method, setMethod] = useState<'pin' | 'code'>(pinEnabled ? 'pin' : 'code');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [channel, setChannel] = useState<OtpChannel>('sms');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resendIn = useCountdown(resendAt);
  const { refs, context } = useFloating({ open: true });

  useEffect(() => {
    const root = document.getElementById('app-root');
    root?.setAttribute('inert', '');
    root?.setAttribute('aria-hidden', 'true');
    return () => {
      root?.removeAttribute('inert');
      root?.removeAttribute('aria-hidden');
    };
  }, []);

  const run = async (action: () => Promise<void>, onError?: (error: unknown) => void) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(describeError(caught, tErrors, i18n).description);
      onError?.(caught);
    } finally {
      setBusy(false);
    }
  };

  const submitPin = (event: FormEvent) => {
    event.preventDefault();
    if (pin.length < PIN_MIN) {
      setPinError(t('lock.pinTooShort'));
      return;
    }
    setPinError(null);
    void run(
      () => onUnlockWithPin(pin),
      (caught) => {
        setPin('');
        if (caught instanceof ApiError && PIN_DEAD_CODES.has(caught.code)) setMethod('code');
      },
    );
  };

  const sendCode = () =>
    run(async () => {
      const next = await onRequestCode(channel);
      setChallenge(next);
      setCode('');
      setResendAt(Date.now() + next.resendAfterSeconds * 1000);
    });

  const submitCode = (value: string) => {
    if (!challenge || value.length !== challenge.codeLength) return;
    void run(
      () => onUnlockWithCode(challenge.challengeId, value),
      () => setCode(''),
    );
  };

  const switchMethod = (next: 'pin' | 'code') => {
    setMethod(next);
    setError(null);
    setPinError(null);
    setPin('');
    setCode('');
  };

  return (
    <FloatingPortal>
      <div className={s.overlay}>
        {recording ? (
          <RecordingIndicator
            className={s.recording}
            size="large"
            status={recording.status}
            elapsedSeconds={recording.elapsedSeconds}
            onStop={recording.stop}
            detail={recording.notice ?? t('lock.recordingContinues')}
          />
        ) : null}
        <FloatingFocusManager context={context} modal initialFocus={0}>
          <div ref={refs.setFloating} className={s.panel} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={textId}>
            <span className={s.icon} aria-hidden="true">
              <Locked size={24} />
            </span>
            <div>
              <h1 id={titleId} className={s.title}>
                {t('lock.title')}
              </h1>
              <p id={textId} className={s.text}>
                {t('lock.description', { minutes: idleMinutes })}
              </p>
            </div>
            {userName ? (
              <div className={s.user}>
                <Avatar name={userName} size="md" />
                <div className={s.userName}>{userName}</div>
              </div>
            ) : null}
            {error ? (
              <Banner tone="error" announce>
                {error}
              </Banner>
            ) : null}

            {method === 'pin' ? (
              <form className={s.form} onSubmit={submitPin} noValidate>
                <FormField label={t('lock.pinLabel')} hint={t('lock.pinHint')} error={pinError ?? undefined}>
                  <Input
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={PIN_MAX}
                    value={pin}
                    disabled={busy}
                    onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, PIN_MAX))}
                  />
                </FormField>
                <div className={s.actions}>
                  <Button type="submit" size="lg" fullWidth loading={busy}>
                    {t('lock.unlock')}
                  </Button>
                  <Button variant="ghost" onClick={() => switchMethod('code')}>
                    {t('lock.useCode')}
                  </Button>
                </div>
              </form>
            ) : challenge ? (
              <form
                className={s.form}
                onSubmit={(event) => {
                  event.preventDefault();
                  submitCode(code);
                }}
              >
                <FormField
                  label={t('lock.codeLabel')}
                  hint={challenge.channel === 'whatsapp' ? t('lock.codeHintWhatsapp', { phone: challenge.phoneMasked }) : t('lock.codeHintSms', { phone: challenge.phoneMasked })}
                >
                  <CodeInput length={challenge.codeLength} value={code} onChange={setCode} onComplete={submitCode} disabled={busy} />
                </FormField>
                <div className={s.actions}>
                  <Button type="submit" size="lg" fullWidth loading={busy} disabled={code.length !== challenge.codeLength}>
                    {t('lock.unlock')}
                  </Button>
                  <Button variant="ghost" onClick={() => void sendCode()} disabled={busy || resendIn > 0}>
                    {resendIn > 0 ? t('lock.resendIn', { seconds: resendIn }) : t('lock.resendCode')}
                  </Button>
                </div>
                {pinEnabled ? (
                  <Button variant="ghost" className={s.secondary} onClick={() => switchMethod('pin')}>
                    {t('lock.usePin')}
                  </Button>
                ) : null}
              </form>
            ) : (
              <div className={s.form}>
                <p className={s.text}>{t('lock.codeIntro')}</p>
                <RadioGroup
                  legend={t('lock.channelLegend')}
                  orientation="horizontal"
                  value={channel}
                  onChange={setChannel}
                  options={[
                    { value: 'sms', label: t('lock.channels.sms') },
                    { value: 'whatsapp', label: t('lock.channels.whatsapp') },
                  ]}
                />
                <div className={s.actions}>
                  <Button size="lg" fullWidth onClick={() => void sendCode()} loading={busy}>
                    {t('lock.sendCode')}
                  </Button>
                  {pinEnabled ? (
                    <Button variant="ghost" onClick={() => switchMethod('pin')}>
                      {t('lock.usePin')}
                    </Button>
                  ) : null}
                </div>
              </div>
            )}
            <Button variant="danger-ghost" className={s.secondary} onClick={onSignOut}>
              {t('lock.signOut')}
            </Button>
          </div>
        </FloatingFocusManager>
      </div>
    </FloatingPortal>
  );
}
